import type { AnalysisState, EncodingChannel, VisualizationType } from '@aibi/contracts';

export type Encodings = Partial<Record<EncodingChannel, string[]>>;

const nonEmpty = (encodings: Record<string, string[]>): Encodings =>
  Object.fromEntries(Object.entries(encodings).filter(([, keys]) => keys.length > 0));

const take = (keys: readonly string[], start: number, count = 1): string[] =>
  keys.slice(start, start + count);

/**
 * Propone la asignacion de campos a canales visuales para un tipo de visualizacion.
 * Permite que la MISMA consulta cambie de tabla a barras, mapa o pivote sin
 * reescribir el Analysis State: solo cambia `visualization.type` y sus encodings.
 *
 * @example
 * suggestEncodings('bar', state); // { x: ['country'], y: ['sales'] }
 */
export function suggestEncodings(
  type: VisualizationType,
  state: Pick<AnalysisState, 'dimensions' | 'measures'>,
): Encodings {
  const dims = state.dimensions.map((dimension) => dimension.key);
  const measures = state.measures.map((measure) => measure.key);

  switch (type) {
    case 'table':
      return nonEmpty({ columns: [...dims, ...measures] });
    case 'grouped-table':
      return nonEmpty({ rows: dims, values: measures });
    case 'pivot':
      return nonEmpty({ rows: take(dims, 0), columns: dims.slice(1), values: measures });
    case 'kpi':
      return nonEmpty({ values: measures });
    case 'bar':
    case 'column':
    case 'line':
    case 'area':
      return nonEmpty({ x: take(dims, 0), color: take(dims, 1), y: measures });
    case 'pie':
    case 'donut':
      return nonEmpty({ color: take(dims, 0), values: take(measures, 0), label: take(dims, 0) });
    case 'scatter':
      return nonEmpty({
        x: take(measures, 0),
        y: take(measures, 1),
        size: take(measures, 2),
        detail: dims,
      });
    case 'treemap':
      return nonEmpty({ detail: dims, size: take(measures, 0), color: take(measures, 1) });
    case 'waterfall':
      return nonEmpty({ x: take(dims, 0), y: take(measures, 0) });
    case 'map':
      return nonEmpty({ geo: take(dims, 0), color: take(measures, 0), size: take(measures, 1) });
    case 'heatmap':
      return nonEmpty({ x: take(dims, 0), y: take(dims, 1), color: take(measures, 0) });
    default:
      // Visualizaciones de plugins: el plugin define sus propios encodings.
      return {};
  }
}
